import { useEffect, useState } from "react";
import useData from "./useData";

const useSKU = () => {
  const { getFromServer, executeAction } = useData();

  const [skus, setSkus] = useState(null);
  const [skusLoaded, setSkusLoaded] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [listings, setListings] = useState([]);

  const getSkus = async (filter) => {
    setSkus(null);
    setSkusLoaded(false);

    const results = await getFromServer({
      entityName: "SKU",
      filter,
    });

    if (results) {
      setSkus(results);
      return results;
    }

    setSkus([]);
    return [];
  };

  useEffect(() => {
    if (!skus) return;
    getCustomFields();
    setSkusLoaded(true);
  }, [skus]);

  const skuListings = async (callId) => {
    const call = await getFromServer({
      entityName: "Call",
      id: callId,
    });
    const client = await getFromServer({
      entityName: "Client",
      id: call.clientId,
    });
    const location = await getFromServer({
      entityName: "Location",
      id: call.locationId,
    });

    // If no listing options, return all SKUs for the client
    if (!client.listingOptions) {
      const allSkus = await getFromServer({
        entityName: "SKU",
        filter: { clientId: client.id },
      });
      return allSkus || [];
    }

    const listingOptions = client.listingOptions;

    let action = "";
    let filter = { clientId: client.id };

    switch (listingOptions.retailLevel) {
      case "national":
        action = "getNationalListings";
        break;

      case "retailer":
        action = "getRetailerListings";
        filter.retailerId = location.retailerId;
        break;

      case "banner":
        action = "getBannerListings";
        filter.bannerId = location.bannerId;
        break;

      case "location":
        action = "getLocationListings";
        filter.locationId = location.id;
        break;

      case "account":
        action = "getLocationListings";
        filter.locationId = location.id;
        break;

      default:
        action = "getNationalListings";
        break;
    }

    const response = await executeAction({
      entityName: "SKUListing",
      action: action,
      data: { ...filter },
    });

    if (!response?.isSuccessful || !Array.isArray(response.data)) {
      return [];
    }

    let listings = [];

    const isListedValue = (value) =>
      value === 1 || value === "1" || value === true || value === "true";

    for (const sku of response.data) {
      const provinceCode = location.province || location.address?.province;
      const code =
        listingOptions.countryLevel === "national" ? "national" : provinceCode;

      const listing = sku.listing;
      const listingMap = listing?.listings || {};
      let skuListing = listingMap?.[code];

      // If provincial code is missing, fall back to national flag when present.
      if (
        (skuListing === null || skuListing === undefined) &&
        code !== "national"
      ) {
        skuListing = listingMap?.national;
      }

      listings.push({
        ...sku,
        listed: isListedValue(skuListing),
      });
    }

    return listings.filter((listing) => listing.listed === true);
  };

  const skuPricing = async (callId) => {
    const call = await getFromServer({
      entityName: "Call",
      id: callId,
    });
    const client = await getFromServer({
      entityName: "Client",
      id: call.clientId,
    });
    const location = await getFromServer({
      entityName: "Location",
      id: call.locationId,
    });

    if (!client.pricingOptions) return null;

    const pricingOptions = client.pricingOptions;

    let action = "";
    let filter = { clientId: client.id };

    switch (pricingOptions.retailLevel) {
      case "national":
        action = "getNationalPricing";
        break;

      case "retailer":
        action = "getRetailerPricing";
        filter.retailerId = location.retailerId;
        break;

      case "banner":
        action = "getBannerPricing";
        filter.bannerId = location.bannerId;
        break;

      case "location":
        action = "getLocationPricing";
        filter.locationId = location.id;
        break;
    }

    const response = await executeAction({
      entityName: "SKUPricing",
      action: action,
      data: { ...filter },
    });

    let prices = [];

    for (const sku of response.data || []) {
      let code =
        pricingOptions.countryLevel === "national"
          ? "national"
          : location.province;
      const pricing = sku.pricing;
      let skuPrice = pricing.prices[code];
      prices.push({
        ...sku,
        listed: skuPrice,
      });
    }

    return prices;
  };

  const getCustomFields = () => {
    if (!Array.isArray(skus) || skus.length === 0) return [];

    let fields = [];

    for (const sku of skus) {
      if (Array.isArray(sku.fields) && sku.fields.length > 0) {
        for (const field of sku.fields) {
          if (!fields.includes(field.name)) {
            fields.push(field.name);
          }
        }
      }
    }

    setCustomFields(fields);
  };

  return {
    getSkus,
    skus,
    getCustomFields,
    customFields,
    skusLoaded,
    skuListings,
    skuPricing,
    listings,
  };
};

export default useSKU;
